import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { concatMap, filter, finalize, map, switchMap } from 'rxjs/operators';

import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { PpwrPackagingInfoDto } from 'src/app/data-transfer/entities/ppwr-entities/ppwr-packaging-info-dto';
import { TechnicalDocumentationStatusDto } from 'src/app/data-transfer/entities/ppwr-entities/technical-documentation-status-dto';
import { SetPpwrApprovedRequestDto } from 'src/app/data-transfer/entities/ppwr-entities/set-ppwr-approved-request-dto';
import { CreateDocRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-request-dto';
import { CreateDocTdRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-td-request';
import { CreateDocFailedResponseDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-failed-response-dto';
import { CreateDocPreRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-pre-request-dto';

import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';

import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import { PackagingUnitNavigationService } from 'src/app/navigation/services/navigation-services/packaging-unit-navigation.service';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';

import { CreateDocDialogComponent } from 'src/app/components/dialogs/create-doc-dialog/create-doc-dialog.component';
import { CreateDocSignersDialogComponent } from 'src/app/components/dialogs/create-doc-signers-dialog/create-doc-signers-dialog.component';
import { CreateDocFailedDialogComponent, CreateDocFailedDialogData } from 'src/app/components/dialogs/create-doc-failed-dialog/create-doc-failed-dialog.component';
import { CreateDocTdDownloadDialogComponent } from 'src/app/components/dialogs/create-doc-td-download-dialog/create-doc-td-download-dialog.component';
import { CreateDocTdDialogComponent } from 'src/app/components/dialogs/create-doc-td-dialog/create-doc-td-dialog.component';
import { CreateDocSignatureDialogComponent } from 'src/app/components/dialogs/create-doc-signature-dialog/create-doc-signature-dialog.component';
import { SimpleAlertDialogComponent } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';

import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { OverviewTableComponent } from 'src/app/components/shared-components/parent-components/overview-table/overview-table.component';
import { PpwrPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';

@Component({
  selector: 'app-packaging-ppwr-overview',
  templateUrl: './packaging-ppwr-overview.component.html',
  styleUrl: './packaging-ppwr-overview.component.scss',
})
export class PackagingPpwrOverviewComponent extends OverviewTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() packagingPart: PackagingPart = PackagingPart.Unit;
  @Input() sortByColumnName?: string;
  @Input() directoryIds?: number[];
  @Input() dataSource!: PaginationDataSource<PpwrPackagingInfoDto>;
  @Output() packagingPartsSelected = new EventEmitter<number[]>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  selectedIds: number[] = [];
  isLoading: Observable<boolean> = of(false);

  private packagingIds: number[] = [];

  private docProblemsSubscription?: Subscription;
  private docWizardSubscription?: Subscription;
  private docGenerationSubscription?: Subscription;
  private technicalDocSubscription?: Subscription;

  readonly DataExchangeRequestStatusDto = DataExchangeRequestStatusDto;
  readonly TechnicalDocumentationStatusDto = TechnicalDocumentationStatusDto;

  readonly displayedColumns = [
    'id',
    'brandName',
    'productName',
    'isCompliant',
    'isApproved',
    'technicalDocumentationStatus',
    'isDocGenerated',
    'dataExchangeRequestStatus',
    'select',
  ];

  constructor(
    private readonly ppwrApiService: PpwrApiService,
    protected translateService: TranslocoService,
    private readonly packagingSystemNavigationService: PackagingSystemNavigationService,
    private readonly packagingUnitNavigationService: PackagingUnitNavigationService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
    protected dialog: MatDialog,
    protected directoryApiService: DirectoryApiService,
    protected importExportApiService: ImportExportApiService,
    protected userApiService: UserApiService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) locale: string,
    private readonly dataExchangeRequestApiService: DataExchangeRequestApiService
  ) {
    super(dialog, directoryApiService, importExportApiService, packagingSystemNavigationService, translateService, spinner, locale);
  }

  ngOnInit(): void {
    this.isLoading = this.dataSource.isLoading;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.directoryIds && this.paginator) {
      this.refreshDataSource();
    }
  }

  ngAfterViewInit(): void {
    this.refreshDataSource();
    this.cd.detectChanges();
    this.dataSource.page.subscribe(page => {
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
      this.packagingIds = page.allIds;
      this.selectedIds = [];
      this.emitSelection();
    });
    this.dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
  }

  onPageChange(event: PageEvent): void {
    this.dataSource.fetchData(event.pageIndex, event.pageSize);
    this.selectedIds = [];
    this.emitSelection();
  }

  private refreshDataSource(): void {
    if (!this.directoryIds || this.directoryIds.length === 0) {
      this.dataSource.clear();
      return;
    }

    const filters = this.directoryIds.map(id => ({ fieldName: 'DirectoryId', value: id.toString() }));
    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, filters);
  }

  getPaginationFieldName(column: string): string {
    switch (column) {
      case 'id':
        return PpwrPaginationFields.Id;
      case 'brandName':
        return PpwrPaginationFields.BrandName;
      case 'productName':
        return PpwrPaginationFields.ProductName;
      case 'isCompliant':
        return PpwrPaginationFields.IsCompliant;
      case 'isApproved':
        return PpwrPaginationFields.IsApproved;
      case 'dataExchangeRequestStatus':
        return PpwrPaginationFields.DataExchangeRequestStatus;
      case 'isDocGenerated':
        return PpwrPaginationFields.IsDocGenerated;
      case 'technicalDocumentationStatus':
        return PpwrPaginationFields.TechnicalDocumentationStatus;
    }
    return '';
  }

  getCaller(columnName: string): string {
    return this.getPaginationFieldName(columnName);
  }

  onSortChange(sort: Sort): void {
    if (sort.active && sort.direction) {
      this.dataSource.sort(sort.active, sort.direction === 'asc');
    } else {
      this.dataSource.removeSorting();
    }
  }

  isAllSelected(): boolean {
    return this.selectedIds.length > 0 && this.selectedIds.length === this.packagingIds.length;
  }

  toggleAllRows(): void {
    this.allItemsSelected = !this.allItemsSelected;
    this.selectedIds = this.allItemsSelected ? [...this.packagingIds] : [];
    this.emitSelection();
  }

  toggleRow(row: PpwrPackagingInfoDto): void {
    const index = this.selectedIds.indexOf(row.id);
    if (index > -1) {
      this.selectedIds.splice(index, 1);
    } else {
      this.selectedIds.push(row.id);
    }
    this.emitSelection();
  }

  isRowSelected(row: PpwrPackagingInfoDto): boolean {
    return this.selectedIds.includes(row.id);
  }

  private getSelectedIds(): number[] {
    return this.selectedIds;
  }

  private emitSelection(): void {
    this.packagingPartsSelected.emit(this.selectedIds);
  }

  protected override onDataExchangeRequestCreated(): void {
    this.resetSelection();
  }

  public clearSelection(): void {
    this.selectedIds = [];
    this.allItemsSelected = false;
    this.emitSelection();
  }

  public resetSelection(): void {
    this.clearSelection();
    this.refreshData();
    this.cd.detectChanges();
  }

  private refreshData(): void {
    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize);
  }

  onApprovalChange(element: PpwrPackagingInfoDto, isApproved: boolean | null): void {
    const previousValue = element.isApproved;
    element.isApproved = isApproved;

    const request: SetPpwrApprovedRequestDto = {
      packagings: [{ id: element.id, version: element.version }],
      isApproved,
    };
    const call = this.packagingPart === PackagingPart.System ? this.ppwrApiService.setPpwrApprovedForSystem(request) : this.ppwrApiService.setPpwrApprovedForUnit(request);

    call.subscribe({
      next: () => {
        this.spinner.hide();
        this.showSnackbar('dataManagement.organizationSettings.messages.saveSuccess', 'app-success', 3000);
      },
      error: () => {
        this.spinner.hide();
        element.isApproved = previousValue;
        this.showSnackbar('dataManagement.organizationSettings.messages.saveError', 'app-error', 4000);
      },
    });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translateService.translate(messageKey), this.translateService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  onDataExchangeRequestClick(element: PpwrPackagingInfoDto): void {
    if (element.dataExchangeRequestId == null) {
      return;
    }
    this.router.navigate([PathResolveService.getDataExchangeRequestsPath(true)], { queryParams: { requestId: element.dataExchangeRequestId } });
  }

  getRequestStatusLabel(status: DataExchangeRequestStatusDto): string {
    return `dataManagement.dataExchangeRequests.status.${DataExchangeRequestStatusDto[status]}`;
  }

  getCompliantOptions(): { name: string; value: any }[] {
    return [
      { name: this.translateService.translate('ppwr.overview.compliant.compliant'), value: true },
      { name: this.translateService.translate('ppwr.overview.compliant.nonCompliant'), value: false },
      { name: this.translateService.translate('ppwr.overview.compliant.noStatement'), value: null },
    ];
  }

  getApprovedOptions(): { name: string; value: any }[] {
    return [
      { name: this.translateService.translate('ppwr.overview.approved.approved'), value: true },
      { name: this.translateService.translate('ppwr.overview.approved.notApproved'), value: false },
      { name: this.translateService.translate('ppwr.overview.approved.noSelection'), value: null },
    ];
  }

  getDocGeneratedOptions(): { name: string; value: any }[] {
    return [
      { name: this.translateService.translate('ppwr.overview.doc.created'), value: true },
      { name: this.translateService.translate('ppwr.overview.doc.notCreated'), value: false },
    ];
  }

  getTdStatusOptions(): { name: string; value: any }[] {
    return [
      { name: this.translateService.translate('ppwr.overview.td.created'), value: this.TechnicalDocumentationStatusDto.Created },
      { name: this.translateService.translate('ppwr.overview.td.notCreated'), value: this.TechnicalDocumentationStatusDto.NotCreated },
      { name: this.translateService.translate('ppwr.overview.td.expired'), value: this.TechnicalDocumentationStatusDto.Expired },
    ];
  }

  getStatusOptions(): { name: string; value: string }[] {
    return [
      { name: this.translateService.translate('dataManagement.dataExchangeRequests.status.Open'), value: 'Open' },
      { name: this.translateService.translate('dataManagement.dataExchangeRequests.status.InProgress'), value: 'InProgress' },
      { name: this.translateService.translate('dataManagement.dataExchangeRequests.status.Submitted'), value: 'Submitted' },
      { name: this.translateService.translate('dataManagement.dataExchangeRequests.status.ReOpened'), value: 'ReOpened' },
      { name: this.translateService.translate('dataManagement.dataExchangeRequests.status.Closed'), value: 'Closed' },
    ];
  }

  createDataExchangeRequest(): void {
    const idsKey = this.packagingPart === PackagingPart.System ? 'packagingSystemIds' : 'packagingUnitIds';
    const subRequestData: any = { allPrincipals: [], [idsKey]: this.getSelectedIds() };
    this.openCreateDataExchangeRequestDialog(subRequestData, dto => this.dataExchangeRequestApiService.createDataExchangeRequest(dto, true));
    this.resetSelection();
  }

  getTagsSettingFunction(): (id: number, tagIds: number[]) => Observable<any> {
    return () => of(undefined);
  }

  getPackagingPartFunction(): (id: number) => Observable<any> {
    return () => of(undefined);
  }

  movePackagingPart(_rootFolder: DirectoryDto, _unreachableFolders: DirectoryDto[]): void {}
  copyPackagingPart(_rootFolder: DirectoryDto, _unreachableFolders: DirectoryDto[]): void {}
  deletePackagingPart(): void {}
  restorePackagingPart(): void {}
  exportPackagingPart(): void {}
  comparePackagingPart(): void {}
  editPackagingPartQuantity(): void {}

  private isPackagingSystem(): boolean {
    return this.packagingPart === PackagingPart.System;
  }

  private fetchDocProblems(requestDto: CreateDocTdRequestDto): Observable<CreateDocFailedResponseDto[]> {
    return this.packagingPart === PackagingPart.System
      ? this.ppwrApiService.getDocProblemsForSystems(requestDto, true)
      : this.ppwrApiService.getDocProblemsForUnits(requestDto, true);
  }

  private generateDocBlob(requestDto: CreateDocRequestDto): Observable<Blob> {
    return this.packagingPart === PackagingPart.System ? this.ppwrApiService.generateDocForSystems(requestDto, true) : this.ppwrApiService.generateDocForUnits(requestDto, true);
  }

  private generateTechnicalDocBlob(requestDto: CreateDocTdRequestDto): Observable<Blob> {
    return this.packagingPart === PackagingPart.System
      ? this.ppwrApiService.generateTechnicalDocumentationForSystems(requestDto, true)
      : this.ppwrApiService.generateTechnicalDocumentationForUnits(requestDto, true);
  }

  createDoc(): void {
    const selectedIds = this.getSelectedIds();
    const packagingElements: CreateDocPreRequestDto[] = selectedIds.map(id => ({ id }));

    this.docProblemsSubscription = this.dialog
      .open(CreateDocTdDialogComponent, getDialogConfig({ mode: 'doc', packagingElements }, '650px'))
      .afterClosed()
      .pipe(
        filter(result => !!result?.confirmed),
        switchMap(result => {
          const requestDto: CreateDocTdRequestDto = { language: result.language, packagingElements };
          this.spinner.show();
          return this.fetchDocProblems(requestDto).pipe(map(problemEntries => ({ problemEntries, requestDto })));
        })
      )
      .subscribe({
        next: ({ problemEntries, requestDto }) => {
          this.spinner.hide();
          if (problemEntries.length === 0) {
            this.startDocWizard(requestDto);
          } else {
            const failedDialogData: CreateDocFailedDialogData = {
              totalCount: packagingElements.length,
              problemEntries,
              isPackagingSystem: this.isPackagingSystem(),
              language: requestDto.language,
            };
            this.dialog
              .open(CreateDocFailedDialogComponent, getDialogConfig(failedDialogData, '750px'))
              .afterClosed()
              .subscribe(result => {
                if (result?.event === DialogActions.CONFIRM) {
                  this.startDocWizard(requestDto);
                }
                this.resetSelection();
              });
          }
        },
        error: () => {
          this.spinner.hide();
          this.openErrorDialog('createDoc.errors.problemCheckFailed');
        },
      });
  }

  private startDocWizard(requestDto: CreateDocTdRequestDto): void {
    this.spinner.show();

    this.docWizardSubscription = this.userApiService
      .getOwnOrganization()
      .pipe(
        concatMap(org => {
          this.spinner.hide();
          return this.dialog
            .open(
              CreateDocDialogComponent,
              getDialogConfig({ manufacturers: org.manufacturerProfiles ?? [], representatives: org.authorizedRepresentativeProfiles ?? [] }, '600px')
            )
            .afterClosed()
            .pipe(map(createDocResult => ({ org, createDocResult })));
        }),
        filter(ctx => !!ctx.createDocResult?.confirmed),
        concatMap(ctx =>
          this.dialog
            .open(CreateDocSignersDialogComponent, getDialogConfig({ managingDirectors: ctx.org.ceoProfiles ?? [], signers: ctx.org.signeeProfiles ?? [] }, '600px'))
            .afterClosed()
            .pipe(map(signeeResult => ({ ...ctx, signeeResult })))
        ),
        filter(ctx => !!ctx.signeeResult?.confirmed),
        concatMap(ctx =>
          this.dialog
            .open(CreateDocSignatureDialogComponent, getDialogConfig({}, '600px'))
            .afterClosed()
            .pipe(map(signatureResult => ({ ...ctx, signatureResult })))
        ),
        filter(ctx => !!ctx.signatureResult?.confirmed),
        map(ctx => ({
          manufacturerId: ctx.createDocResult.manufacturerId,
          authorizedRepresentativeId: ctx.createDocResult.authorizedRepresentativeId,
          ceoId: ctx.signeeResult.ceoId,
          signeeId: ctx.signeeResult.signeeId,
          language: requestDto.language,
          packagingElements: requestDto.packagingElements,
        })),
        finalize(() => this.resetSelection())
      )
      .subscribe({
        next: finalRequestDto => this.generateDoc(finalRequestDto),
        error: () => {
          this.spinner.hide();
          this.openErrorDialog('createDoc.errors.wizardDataLoadFailed');
        },
      });
  }

  private generateDoc(requestDto: CreateDocRequestDto): void {
    this.spinner.show();

    this.docGenerationSubscription = this.generateDocBlob(requestDto).subscribe({
      next: blob => {
        this.spinner.hide();
        this.openDocDownloadDialog(blob, requestDto.packagingElements.length, true);
      },
      error: () => {
        this.spinner.hide();
        this.openErrorDialog('createDoc.errors.docGenerationFailed');
      },
    });
  }

  createTechnicalDoc(): void {
    const selectedIds = this.getSelectedIds();
    const packagingElements: CreateDocPreRequestDto[] = selectedIds.map(id => ({ id }));

    this.technicalDocSubscription = this.dialog
      .open(CreateDocTdDialogComponent, getDialogConfig({ mode: 'td', packagingElements }, '650px'))
      .afterClosed()
      .pipe(
        filter(result => !!result?.confirmed),
        switchMap(result => {
          this.spinner.show();
          const requestDto: CreateDocTdRequestDto = { language: result.language, packagingElements };
          return this.generateTechnicalDocBlob(requestDto);
        }),
        finalize(() => this.resetSelection())
      )
      .subscribe({
        next: blob => {
          this.spinner.hide();
          this.openDocDownloadDialog(blob, packagingElements.length, false);
        },
        error: () => {
          this.spinner.hide();
          this.openErrorDialog('createDoc.errors.tdGenerationFailed');
        },
      });
  }

  private openDocDownloadDialog(blob: Blob, fileCount: number, isDoc: boolean): void {
    const prefix = isDoc ? 'DoC_PPWR_' : 'TD_PPWR_';
    this.dialog.open(
      CreateDocTdDownloadDialogComponent,
      getDialogConfig(
        { isDoc, blob, fileName: `${prefix}${new Date().toISOString().slice(0, 10)}.zip`, fileCount, fileSizeMb: blob?.size ? blob.size / (1024 * 1024) : 0 },
        '480px'
      )
    );
  }

  private openErrorDialog(translationKey: string): void {
    this.dialog.open(
      SimpleAlertDialogComponent,
      getDialogConfig({ title: this.translateService.translate('common.text.error'), messages: [this.translateService.translate(translationKey)], icon: 'error' }, '500px')
    );
  }

  getPackagingHref(element: PpwrPackagingInfoDto): string {
    const path =
      this.packagingPart === PackagingPart.System
        ? PathResolveService.getUpdatePackagingSystemPath(true, element.id)
        : PathResolveService.getUpdatePackagingUnitPath(true, element.id);
    return '/' + path;
  }

  onIdClick(event: MouseEvent, element: PpwrPackagingInfoDto): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
      return;
    }
    event.preventDefault();
    if (this.packagingPart === PackagingPart.System) {
      this.packagingSystemNavigationService.navigateToPackagingSystem(element.id);
    } else {
      this.packagingUnitNavigationService.navigateToPackagingUnit(element.id);
    }
  }

  public showLoadingSpinner(): void {
    this.dataSource.setLoading(true);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.docProblemsSubscription?.unsubscribe();
    this.docWizardSubscription?.unsubscribe();
    this.docGenerationSubscription?.unsubscribe();
    this.technicalDocSubscription?.unsubscribe();
  }
}
